import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { registrationService } from '@/services/registration.service';
import toast from 'react-hot-toast';
import { 
  CheckCircle, 
  XCircle, 
  Mail, 
  ArrowRight,
  RefreshCw
} from 'lucide-react';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verificationToken = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Auto-verify on mount
  useEffect(() => {
    if (!verificationToken) {
      setError('No verification token provided. Please use the link from your email.');
      setVerifying(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        const result = await registrationService.verifyEmail(verificationToken);
        if (result.success) {
          setVerified(true);
          toast.success('Email verified successfully!');
          // Redirect to login or dashboard after 3 seconds
          setTimeout(() => {
            navigate(result.redirectUrl || '/login');
          }, 3000);
        } else {
          setError(result.message || 'Email verification failed.');
        }
      } catch (error: any) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('expired')) {
          setError('This verification link has expired. Please request a new one.');
        } else if (errorMessage.includes('invalid')) {
          setError('This verification link is invalid. Please check your email for the correct link.');
        } else if (errorMessage.includes('already')) {
          setError('This email has already been verified. You can log in now.');
        } else {
          setError(error.message || 'Email verification failed.');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [verificationToken, navigate]);

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      const result = await registrationService.resendVerification(email);
      if (result.success) {
        toast.success('Verification email sent!');
        
        // Disable resend for rate limiting (5 minutes)
        setResendDisabled(true);
        setResendCountdown(300); // 5 minutes in seconds
        
        // Start countdown
        const interval = setInterval(() => {
          setResendCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        } else {
        // Handle cases like "Email is already verified"
        toast.error(result.message || 'Unable to resend verification email.');
      }
    } catch (error: any) {
      if (error.message.includes('many')) {
        toast.error('Too many requests. Please wait before trying again.');
        setResendDisabled(true);
        setResendCountdown(300);
      } else {
        toast.error(error.message || 'Failed to resend verification email.');
      }
    } finally {
      setResending(false);
    }
  };

  // Format countdown timer
  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (verifying) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-600">Verifying your email...</p>
      </div>
    );
  }

  // Success state
  if (verified) {
    return (
      <div className="w-full">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Email Verified!</h2>
          <p className="text-gray-600 mt-2">
            Your email has been successfully verified.
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <p className="text-sm text-green-800 text-center">
            You can now access all features of your account.
            Redirecting to login...
          </p>
        </div>

        <div className="text-center">
          <Link 
            to="/login" 
            className="btn-primary inline-flex items-center gap-2"
          >
            Continue to Login
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <XCircle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900">Verification Failed</h2>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
        <p className="text-sm text-red-800 text-center mb-4">
          {error}
        </p>
      </div>

      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-3">Need a new verification email?</h3>
          <p className="text-sm text-gray-600 mb-4">
            Enter your email address below and we'll send you a new verification link.
          </p>
          
          <div className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field pl-10"
                placeholder="Enter your email address"
              />
            </div>
            
            <button
              onClick={handleResendVerification}
              disabled={resending || resendDisabled || !email}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {resending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : resendDisabled ? (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Resend in {formatCountdown(resendCountdown)}
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Resend Verification Email
                </>
              )}
            </button>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Already verified your email?
          </p>
          <Link 
            to="/login" 
            className="text-primary-700 hover:text-primary-800 font-medium"
          >
            Go to login
          </Link>
        </div>
      </div>
    </div>
  );
}